/**
 * Sdílená plocha stromu planneru — prezentace typů (ikony + `typeDefinitions`) a akce řádků.
 *
 * Akce jsou čistě **navigační události**: tlačítka jen otevírají agendu, takže vyvolávají tytéž
 * navigace jako výběr řádku.
 */
import { useEffect, useMemo, useRef } from 'react';
import { CalendarSearch, Plus } from 'lucide-react';
import {
  TreeBoxIcon,
  TreeChainIcon,
  TreeFolderIcon,
  TreeUsersIcon,
  type TreeData,
  type TreeNodeType,
  type TreeRowAction,
} from '@/shared/ui';
import { useDetailZone } from '@/shared/navigation/react/useDetailZone';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { projectFormAgenda } from './project';
import { productDetailAgenda, productFormAgenda } from './product';
import {
  SERIE_TAB_KEYS,
  serieDetailAgenda,
  serieFormAgenda,
  termDetailAgenda,
  type SerieTabKey,
} from './serie';

// Typové ikony dle PHX-TREE-01 (stejné jako Columbus): barvu dle hloubky úrovně dodává TreeView,
// velikost si nesou samy → žádný sizing wrapper. project→složka, product→krabice, serie→řetěz,
// term→osoby.
const ProjectIcon = TreeFolderIcon;
const ProductIcon = TreeBoxIcon;
const SerieIcon = TreeChainIcon;
const TermIcon = TreeUsersIcon;

export interface VenuaPlannerTreeTypeLabels {
  project: string;
  product: string;
  serie: string;
  term: string;
}

/** Sdílené `typeDefinitions` (ikona + lokalizovaný název typu) pro oba tree modely. */
export function buildVenuaPlannerTypeDefinitions(
  labels: VenuaPlannerTreeTypeLabels
): TreeNodeType[] {
  return [
    { id: 'project', label: labels.project, icon: ProjectIcon },
    { id: 'product', label: labels.product, icon: ProductIcon },
    { id: 'serie', label: labels.serie, icon: SerieIcon },
    { id: 'term', label: labels.term, icon: TermIcon },
  ];
}

/**
 * Seriál i termín mají stejnou sadu záložek (vrstvy dat), takže přepnutí na jiný záznam ve stromu
 * zůstane na té, kterou uživatel právě sleduje — a to i napříč typy (seriál → termín a zpět).
 * Předá ji jádro z nahrazovaného detailu; vrstvy si parametry navzájem nečtou. Když žádný detail
 * otevřený není (není od koho dědit), dodá záložku paměť stromu — viz `useVenuaPlannerTreeNavigation`.
 */
const INHERIT_SERIE_TAB = { inheritStateKeys: ['tab'] } as const;

/** Agendy se sadou záložek `SERIE_TAB_KEYS` — jen jejich záložku si strom pamatuje. */
const SERIE_TAB_AGENDA_IDS: readonly string[] = [serieDetailAgenda.id, termDetailAgenda.id];

/** Surová hodnota `tab` z detail zóny (list nezná codec cizí vrstvy) → typový klíč záložky. */
function toSerieTab(value: unknown): SerieTabKey | undefined {
  return SERIE_TAB_KEYS.find((key) => key === value);
}

/**
 * Navigační události stromu — každá jen otevře příslušnou agendu přes `openDetail`: strom je list,
 * takže jeho výběr určuje obsah detail zóny. Detail se otevře jako okno nad kalendářem, který
 * detail zónu drží; po jeho zavření je kalendář zase vidět (nikdy se neodmountoval).
 */
export interface VenuaPlannerTreeNavigation {
  openProjectDetail: (projectId: string) => void;
  openProductDetail: (productId: string) => void;
  openSerieDetail: (productId: string, serieId: string) => void;
  openTermDetail: (productId: string, serieId: string, termId: string) => void;
  addProduct: (projectId: string) => void;
  addSerie: (productId: string) => void;
  /** Otevře detail seriálu rovnou s dialogem „Přidat termíny" (z „+" u seriálu). */
  addSerieTerms: (productId: string, serieId: string) => void;
}

export function useVenuaPlannerTreeNavigation(): VenuaPlannerTreeNavigation {
  const navigator = useNavigator();
  // Co je právě v detail panelu — list se podle toho rozhoduje, co otevřít (viz `openTermDetail`).
  const detailZone = useDetailZone();
  const hasDetail = detailZone !== null;
  const openTab = toSerieTab(detailZone?.state['tab']);

  /*
   * Paměť naposledy sledované záložky. Otevřený detail ji předá sám (jádro přes `inheritStateKeys`),
   * paměť pokrývá díru mezi dvěma detaily: „otevřu yield → zavřu detail → mrknu do kalendáře →
   * otevřu jiný seriál" skončí zase na yieldu, ne na základních údajích.
   *
   * Ref, ne stav ani store: hodnotu čte až handler při kliku (žádný render na ní nevisí) a platnost
   * má právě po dobu otevřené plánovací agendy — strom se pod detailem neodmountuje, takže přežije
   * libovolný počet otevření a zavření detailu, ale nepřenáší se do jiného okna ani přes reload
   * (po něm záložku nese URL otevřeného detailu).
   */
  const lastTabRef = useRef<SerieTabKey | undefined>(undefined);
  const tracksTab = detailZone !== null && SERIE_TAB_AGENDA_IDS.includes(detailZone.agendaId);
  useEffect(() => {
    if (tracksTab) lastTabRef.current = openTab;
  }, [tracksTab, openTab]);

  return useMemo(() => {
    // Záložka nového detailu: z otevřeného ji předá jádro, bez něj ji dodá paměť (a bez paměti
    // se detail otevře na výchozí záložce).
    const serieTabOptions = () => {
      if (hasDetail) return INHERIT_SERIE_TAB;
      const remembered = lastTabRef.current;
      return remembered ? { state: { tab: remembered } } : undefined;
    };
    // Záložka, podle které se rozhoduje klik na termín — ze stejného zdroje jako `serieTabOptions`.
    const activeTab = () => (hasDetail ? openTab : lastTabRef.current);

    return {
      openProjectDetail: (projectId) => void navigator.openDetail(projectFormAgenda, { projectId }),
      openProductDetail: (productId) =>
        void navigator.openDetail(productDetailAgenda, { productId }),
      openSerieDetail: (productId, serieId) =>
        void navigator.openDetail(serieDetailAgenda, { productId, serieId }, serieTabOptions()),
      // Na Yield Managementu se termíny prohlížejí v tabulce seriálu, ne ve vlastním detailu —
      // klik na termín tam proto otevře seriál s tímto termínem rozbaleným.
      openTermDetail: (productId, serieId, termId) =>
        void (activeTab() === 'calculation'
          ? navigator.openDetail(
              serieDetailAgenda,
              { productId, serieId },
              { state: { tab: 'calculation', calcTermId: termId } }
            )
          : navigator.openDetail(
              termDetailAgenda,
              { productId, serieId, termId },
              serieTabOptions()
            )),
      addProduct: (projectId) => void navigator.openDetail(productFormAgenda, { projectId }),
      addSerie: (productId) => void navigator.openDetail(serieFormAgenda, { productId }),
      addSerieTerms: (productId, serieId) =>
        void navigator.openDetail(
          serieDetailAgenda,
          { productId, serieId, addTerms: true },
          serieTabOptions()
        ),
    };
  }, [navigator, hasDetail, openTab]);
}

export interface VenuaPlannerTreeActionLabels {
  /** Skok na záznam v kalendáři (zavře otevřený detail a odscrolluje kalendář). */
  locate: string;
  addProduct: string;
  addSerie: string;
  addTerms?: string;
}

export interface VenuaPlannerTreeActionOptions {
  /** Skok na záznam v kalendáři — bez handleru se akce nevykreslí. */
  onLocate?: (nodeId: string) => void;
  /** Handler pro „přidat termíny“ u seriálu (kalendář je zakládá i přes „+“ na dni). */
  onAddTerms?: (serieId: string) => void;
}

/**
 * Sestaví akce řádků stromu pro `TreeView.actionsByType`.
 *
 * Detail se otevírá VÝBĚREM řádku, takže tady žádné tužky nejsou — místo nich má každý řádek skok
 * na svůj záznam v kalendáři. Pluska zakládají produkt/seriál/termíny.
 *
 * `nodesById` slouží k dohledání `productId` u seriálu (`serie.parentId`).
 */
export function buildVenuaPlannerTreeActions(
  nav: VenuaPlannerTreeNavigation,
  labels: VenuaPlannerTreeActionLabels,
  opts: VenuaPlannerTreeActionOptions = {}
): TreeRowAction[] {
  const actions: TreeRowAction[] = [];

  if (opts.onLocate) {
    actions.push({
      id: 'locate-in-calendar',
      label: labels.locate,
      icon: CalendarSearch,
      appliesTo: ['project', 'product', 'serie', 'term'],
      onClick: opts.onLocate,
    });
  }

  actions.push(
    {
      id: 'add-product',
      label: labels.addProduct,
      icon: Plus,
      appliesTo: ['project'],
      onClick: (projectId) => nav.addProduct(projectId),
    },
    {
      id: 'add-serie',
      label: labels.addSerie,
      icon: Plus,
      appliesTo: ['product'],
      onClick: (productId) => nav.addSerie(productId),
    }
  );

  if (opts.onAddTerms) {
    actions.push({
      id: 'add-terms',
      label: labels.addTerms ?? '',
      icon: Plus,
      appliesTo: ['serie'],
      onClick: opts.onAddTerms,
    });
  }

  return actions;
}

/** Aktivace uzlu výběrem — otevře detail dle typu. Opět jen navigační událost. */
export function navigateVenuaPlannerTreeNode(
  nav: VenuaPlannerTreeNavigation,
  nodesById: TreeData['nodesById'],
  nodeId: string
): void {
  const node = nodesById[nodeId];
  if (!node) return;
  if (node.type === 'project') {
    nav.openProjectDetail(nodeId);
  } else if (node.type === 'product') {
    nav.openProductDetail(nodeId);
  } else if (node.type === 'serie' && node.parentId) {
    nav.openSerieDetail(node.parentId, nodeId);
  } else if (node.type === 'term' && node.parentId) {
    const productId = nodesById[node.parentId]?.parentId;
    if (productId) nav.openTermDetail(productId, node.parentId, nodeId);
  }
}
